/*
 * Copyright (C) 2024 Webformat S.r.l.
 * https://www.webformat.com
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program. If not, see <https://www.gnu.org/licenses/>.
 */

import {Action, getModule, Module, Mutation, MutationAction, VuexModule} from 'vuex-module-decorators';
import store from '@/store';
import cart from '@/store/modules/cart';
import {initState} from '@/store/stateinit';
import to from 'await-to-js';
import handleError from '@/commons/errorHandler';
import {clearJWT, forgotPassword, getUserInfo, login, setJWT, userRegistration} from '../api';
import {Address, Customer, LoginRequest, RegistrationRequest} from '../models';

@Module({
    namespaced: true,
    name: 'user',
    store,
    dynamic: true,
})
class UsersModule extends VuexModule {
    public customer: Customer = initState('customer');
    public token: string = initState('token');
    public addressErrors: boolean[] = [];

    get isLoggedin(): boolean {
        return this.token.toString() !== '';
    }

    get shippingAddress(): Address | undefined {
        return this.customer.addresses.find((a: Address) => a.default_shipping);
    }

    get billingAddress(): Address | undefined {
        return this.customer.addresses.find((a: Address) => a.default_billing);
    }

    get hasAddresses(): boolean {
        return this.customer.addresses.length > 0;
    }

    get idCustomer(): any {
        return this.customer.id;
    }

    @Mutation
    public resetAddressErrors() {
        this.customer.addresses.forEach((a, i, addresses) => {
            if (a.error === true) {
                delete a.error;
                addresses.splice(i, 1);
                addresses.push(a);
            }
        });
    }

    @Mutation
    public setAddressError(id: number) {
        this.customer.addresses.forEach((a, i, addresses) => {
            if (a.id === id) {
                a.error = true;
                addresses.splice(i, 1);
                addresses.push(a);
            }
        });
    }

    @Mutation
    public setCustomer(customer: Customer) {
        this.customer = customer;
    }

    @Mutation
    public setEmail(email: string) {
        this.customer.email = email;
    }

    @Mutation
    public setToken(token: string) {
        this.token = token;
    }

    @Action
    public async login(loginRequest: LoginRequest): Promise<string> {
        const data = await to(login(loginRequest));
        const [err]: any = data;
        let [, token]: any = data;

        return new Promise((resolve, reject) => {
            if (err) {
                clearJWT();
                token = '';
                handleError(err, false);
                reject(err);
            }

            if (token) {
                setJWT(token);
                resolve(token);

                cart.setToken('');
            }

            this.setToken(token);
        });
    }

    @Action
    public async sendForgotPassword(email: string): Promise<boolean> {
        return forgotPassword(email);
    }

    @Action({rawError: true})
    public async register(data: RegistrationRequest): Promise<boolean> {
        return userRegistration(data);
    }

    @MutationAction({mutate: ['customer']})
    public async getMe() {
        const [, customer] = await to(getUserInfo());
        return {customer};
    }
}

export default getModule(UsersModule);
